import { ALL_WEEKDAYS, DEFAULT_LANGUAGE, DEFAULT_WEEK_START, WEEKDAY } from '@wix/wix-events-commons-statics';
import moment from 'moment-timezone';
import { getNarrowWeekday, getShortWeekday, getWeekday } from './formatter';
export { moment };
export const DEFAULT_EVENT_END_DATE_OFFSET = 4;
export const now = (timeZoneId) => moment.tz(timeZoneId);
export const getMomentDate = (date, timeZoneId) => moment.tz(date, timeZoneId);
export const getUtcOffset = (date, timeZoneId) => getMomentDate(date, timeZoneId).utcOffset();
export const getDurationInWeeks = (end, start) => moment.duration(end.diff(start)).asWeeks();
export const getDurationInDays = (end, start) => moment.duration(end.diff(start)).asDays();
export const getDurationInHours = (end, start) => moment.duration(end.diff(start)).asHours();
export const getDurationInMinutes = (end, start) => moment.duration(end.diff(start)).asMinutes();
export const getDurationInMilliseconds = (end, start) => moment.duration(end.diff(start)).asMilliseconds();
export const getHours = (date) => date.hours();
export const getMinutes = (date) => date.minutes();
export const getDayOfWeek = (date) => date.day();
export const getWeek = (date) => date.week();
export const getDayOfMonth = (date) => date.date();
export const getWeekDayIndexInMonth = (date) => {
    const startOfMonth = date.clone().startOf('month');
    const startOfMonthWeekDay = getDayOfWeek(startOfMonth);
    const dateWeekDay = getDayOfWeek(date);
    const startOfMonthWeek = getWeek(startOfMonth);
    const dateWeek = getWeek(date);
    const dateWeekThisYear = dateWeek < startOfMonthWeek ? getWeek(subtractWeeks(date, 1)) + dateWeek : dateWeek;
    return dateWeekThisYear - startOfMonthWeek - (dateWeekDay < startOfMonthWeekDay ? 1 : 0);
};
export const getWeekDays = (firstWeekDay = DEFAULT_WEEK_START) => {
    const firstWeekDayIndex = ALL_WEEKDAYS.indexOf(firstWeekDay);
    return ALL_WEEKDAYS.slice(firstWeekDayIndex).concat(ALL_WEEKDAYS.slice(0, firstWeekDayIndex));
};
export const getWeekDayNames = (locale = DEFAULT_LANGUAGE, firstWeekDay = DEFAULT_WEEK_START) => getWeekDays(firstWeekDay).map(weekDay => getShortWeekdayLabel(weekDay, locale));
export const getFirstWeekDay = (locale = DEFAULT_LANGUAGE) => {
    try {
        const intlLocale = new Intl.Locale(locale);
        // @ts-expect-error
        const weekDay = intlLocale.weekInfo?.firstDay ?? intlLocale.getWeekInfo?.().firstDay ?? 7;
        return weekDay % 7;
    }
    catch (e) {
        return WEEKDAY.SUNDAY;
    }
};
export const getTimeText = (date, locale = DEFAULT_LANGUAGE) => {
    const intl = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
    return intl.format(date);
};
export const getFullMonthText = (date, locale = DEFAULT_LANGUAGE, timeZone) => {
    const intl = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', timeZone });
    return intl.format(date);
};
export const getDayMonthText = (date, locale = DEFAULT_LANGUAGE, timeZone) => {
    const intl = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone });
    return intl.format(date);
};
export const getWeekdayLabel = (weekday, locale = DEFAULT_LANGUAGE) => {
    const date = new Date('2017-01-03T00:00:00');
    date.setDate(1 + weekday);
    return getWeekday(date, locale);
};
export const getShortWeekdayLabel = (weekday, locale = DEFAULT_LANGUAGE) => {
    const date = new Date('2017-01-03T00:00:00');
    date.setDate(1 + weekday);
    return getShortWeekday(date, locale);
};
export const getNarrowWeekdayLabel = (weekday, locale = DEFAULT_LANGUAGE) => {
    const date = new Date('2017-01-03T00:00:00');
    date.setDate(1 + weekday);
    return getNarrowWeekday(date, locale);
};
export const guessUserTimeZoneId = () => moment.tz.guess(true) || 'Europe/London';
export const addMinutes = (date, minutes) => date.clone().add(minutes, 'minutes');
export const addHours = (date, hours) => date.clone().add(hours, 'hours');
export const addDays = (date, days) => date.clone().add(days, 'days');
export const addDaysFromNow = (days) => addDays(now(), days).toISOString();
export const addWeeks = (date, weeks) => date.clone().add(weeks, 'weeks');
export const addMonths = (date, months) => date.clone().add(months, 'months');
export const subtractMinutes = (date, minutes) => date.clone().subtract(minutes, 'minutes');
export const subtractDays = (date, days) => date.clone().subtract(days, 'days');
export const subtractWeeks = (date, weeks) => date.clone().subtract(weeks, 'weeks');
export const subtractMonths = (date, months) => date.clone().subtract(months, 'months');
export const getDefaultEventStartDate = (timezone) => getMomentDate(addDaysFromNow(14), timezone).hours(19).minutes(0).seconds(0).milliseconds(0).toISOString();
export const getDefaultEventEndDate = (startDate) => getMomentDate(startDate).clone().add(DEFAULT_EVENT_END_DATE_OFFSET, 'hours').toISOString();
export const getStartOfDay = (date) => date.clone().startOf('day');
export const getEndOfDay = (date) => date.clone().endOf('day');
export const getDaysBetweenWeekDays = (weekDays) => weekDays.map((weekDay, index) => index === weekDays.length - 1 ? 7 - weekDay + weekDays[0] : weekDays[index + 1] - weekDay);
export const getNthWeekDayOfNthMonth = (date, monthsToAdd) => {
    const weekDayIndexInMonth = getWeekDayIndexInMonth(date);
    const startOfMonth = addMonths(date, monthsToAdd).startOf('month').hours(getHours(date)).minutes(getMinutes(date));
    const startOfMonthWeekDay = getDayOfWeek(startOfMonth);
    const dateWeekDay = getDayOfWeek(date);
    const dateWithWeeksAdded = addWeeks(startOfMonth, weekDayIndexInMonth);
    if (startOfMonthWeekDay === dateWeekDay) {
        return dateWithWeeksAdded;
    }
    else if (dateWeekDay < startOfMonthWeekDay) {
        return addDays(dateWithWeeksAdded, 7 - startOfMonthWeekDay + dateWeekDay);
    }
    else {
        return addDays(dateWithWeeksAdded, dateWeekDay - startOfMonthWeekDay);
    }
};
export const getLastWeekDayOfNthMonth = (date, monthsToAdd) => {
    const endOfMonth = addMonths(date, monthsToAdd)
        .endOf('month')
        .hours(getHours(date))
        .minutes(getMinutes(date))
        .seconds(0)
        .milliseconds(0);
    const endOfMonthWeekDay = getDayOfWeek(endOfMonth);
    const dateWeekDay = getDayOfWeek(date);
    if (endOfMonthWeekDay === dateWeekDay) {
        return endOfMonth;
    }
    else if (dateWeekDay < endOfMonthWeekDay) {
        return subtractDays(endOfMonth, endOfMonthWeekDay - dateWeekDay);
    }
    else {
        return subtractDays(endOfMonth, endOfMonthWeekDay + 7 - dateWeekDay);
    }
};
export const isLastWeekDayOfMonth = (date) => date.month() !== addWeeks(date, 1).month();
